/* eslint-disable jsx-a11y/media-has-caption */
import { useHistoryVideo } from '@/app/main/subadmin/livestreaming/history/HistoryProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MuLawDecoder, PcmAudioPlayer } from 'ui3';
import H264Controls from '@/app/main/subadmin/livestreaming/history/PBJ/H264VideoPlayer/H264Controls';
import { getHistoryVideo } from '@/app/services/liveStreaming/liveStreaming';

const MjpegVideoPlayer = ({ url, audioUrl, roomData, setIsLoading }) => {
	const imgRef = useRef(null);
	const boxRef = useRef(null);
	const { volume, setVolume } = useHistoryVideo();
	const [error, setError] = useState('');
	const [isBuffering, setIsBuffering] = useState(false);
	const pcmPlayerRef = useRef(null);
	const volumeRef = useRef(volume);
	const isPausedRef = useRef(false);
	const abortControllerRef = useRef(null);
	const audioAbortControllerRef = useRef(null);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(1200); // 20 minutes in seconds
	const [isPlaying, setIsPlaying] = useState(true);
	const [isSeeking, setIsSeeking] = useState(false);
	const [pendingSeekTime, setPendingSeekTime] = useState(0);
	const [baseUrl, setBaseUrl] = useState('');
	const [originalStart, setOriginalStart] = useState(null);
	const [originalStop, setOriginalStop] = useState(null);
	const [currentUrl, setCurrentUrl] = useState(url);
	const intervalRef = useRef(null);
	const lastApiCallRef = useRef(0);

	// Function to fetch stream URL from API with throttling
	const fetchStreamUrl = useCallback(async (startDate, endDate) => {
		try {
			// Throttle API calls to prevent 429 errors (minimum 1 second between calls)
			const now = Date.now();
			const timeSinceLastCall = now - lastApiCallRef.current;
			if (timeSinceLastCall < 1000) {
				await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastCall));
			}
			lastApiCallRef.current = Date.now();
			
			const formattedStartDate = startDate.toISOString().replace('T', ' ').replace('Z', '');
			const formattedEndDate = endDate.toISOString().replace('T', ' ').replace('Z', '');
			
			const response = await getHistoryVideo(formattedStartDate, formattedEndDate, roomData?.id);
			return response?.data;
		} catch (error) {
			return null;
		}
	}, [roomData?.id]);

	// Initialize audio player if audio URL is provided
	useEffect(() => {
		if (audioUrl && !pcmPlayerRef.current) {
			const pcmPlayer = new PcmAudioPlayer();
			pcmPlayerRef.current = pcmPlayer;
			pcmPlayer.SetAudioVolumeFromSettings(volumeRef.current);
		}
	}, [audioUrl]);

	// Update volume when volume changes
	useEffect(() => {
		volumeRef.current = volume;
		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(volume);
		}
	}, [volume]);

	// Handle audio stream for G.711 uLaw
	const processAudioStream = useCallback(async (audioStreamUrl) => {
		if (!audioStreamUrl || !pcmPlayerRef.current) return;

		const audioAbortController = new AbortController();
		audioAbortControllerRef.current = audioAbortController;

		try {
			const response = await fetch(audioStreamUrl, {
				credentials: 'same-origin',
				signal: audioAbortController.signal
			});

			if (!response.ok) {
				return;
			}

			const reader = response.body.getReader();
			const muLawDecoder = new MuLawDecoder();

			const processAudioData = async () => {
				try {
					while (true) {
						const { done, value } = await reader.read();
						if (done) break;

						if (isPausedRef.current) {
							continue; // Skip processing if paused
						}

						// Process G.711 uLaw audio data
						// G.711 uLaw is typically 8kHz, 1 channel, 8 bits per sample
						const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(value);
						pcmPlayerRef.current.AcceptBuffer([pcm16Bit], 8000); // 8kHz sample rate for G.711
					}
				} catch (error) {
					// Ignore AbortError, it's expected when pausing
				}
			};

			processAudioData();
		} catch (error) {
			// Ignore AbortError, it's expected when pausing
		}
	}, []);

	// Parse URL parameters and initialize
	useEffect(() => {
		if (!url) return;

		try {
			// Extract the direct URL from proxy if needed for parameter parsing
			let directUrl = url;
			
			// Check if this is a proxy URL and extract the direct URL
			if (url.includes('stream-proxy?url=')) {
				const proxyUrl = new URL(url);
				const encodedUrl = proxyUrl.searchParams.get('url');
				if (encodedUrl) {
					directUrl = decodeURIComponent(encodedUrl);
				}
			}
			
			const urlObj = new URL(directUrl);
			const startParam = urlObj.searchParams.get('start');
			const stopParam = urlObj.searchParams.get('stop');
			
			if (startParam && stopParam) {
				setOriginalStart(new Date(startParam));
				setOriginalStop(new Date(stopParam));
				const durationMs = new Date(stopParam) - new Date(startParam);
				setDuration(Math.floor(durationMs / 1000)); // Convert to seconds
			}
			
			// Keep the original URL (with proxy) for initial load
			setCurrentUrl(url);
		} catch (e) {
			// Ignore URL parsing errors
		}
	}, [url]);

	// Time tracking functions
	const startTimeTracking = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		
		intervalRef.current = setInterval(() => {
			if (!isPausedRef.current && !isSeeking) {
				setCurrentTime(prev => {
					const newTime = prev + 1;
					return newTime >= duration ? duration : newTime;
				});
			}
		}, 1000);
	}, [duration, isSeeking]);

	const stopTimeTracking = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	// Initialize MJPEG stream
	useEffect(() => {
		if (!currentUrl) return;

		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		// Use img element for MJPEG streams
		const imgElement = imgRef.current;
		if (imgElement) {
			imgElement.src = currentUrl;
			imgElement.crossOrigin = 'anonymous';
			
			const handleImgLoad = () => {
				setError(''); // Clear any previous errors
				setIsLoading(false);
				
				// Only start time tracking if we're not paused
				// Don't override the playing state here, let the handlePlayPause manage it
				if (!isPausedRef.current) {
						startTimeTracking();
				}
			};
			
			const handleImgError = (e) => {
				setError('Failed to load MJPEG stream');
				setIsLoading(false);
				setIsPlaying(false);
			};
			
			imgElement.addEventListener('load', handleImgLoad);
			imgElement.addEventListener('error', handleImgError);
			
			// Start audio stream if available
			if (audioUrl && !isPausedRef.current) {
				processAudioStream(audioUrl);
			}
			
			return () => {
				imgElement.removeEventListener('load', handleImgLoad);
				imgElement.removeEventListener('error', handleImgError);
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}
				if (audioAbortControllerRef.current) {
					audioAbortControllerRef.current.abort();
				}
				if (pcmPlayerRef.current) {
					pcmPlayerRef.current.Reset();
				}
				stopTimeTracking();
			};
		}
	}, [currentUrl, audioUrl, processAudioStream, startTimeTracking, stopTimeTracking]);

	// Handle audio stream changes separately to avoid restarting video
	useEffect(() => {
		if (audioUrl && pcmPlayerRef.current && !isPausedRef.current) {
			// Stop current audio stream if running
			if (audioAbortControllerRef.current) {
				audioAbortControllerRef.current.abort();
			}
			
			// Start new audio stream
			processAudioStream(audioUrl);
		}
	}, [audioUrl, processAudioStream]);

	const handleVolumeChange = (newVolume) => {
		setVolume(newVolume);
		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(newVolume);
		}
	};

	// Control handlers
	const handlePlayPause = useCallback(() => {
		if (isPlaying) {
			// Pause
			setIsPlaying(false);
			isPausedRef.current = true;
			stopTimeTracking();
			
			// Stop audio
			if (pcmPlayerRef.current) {
				pcmPlayerRef.current.Pause();
			}
			
			// Stop any ongoing requests
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			if (audioAbortControllerRef.current) {
				audioAbortControllerRef.current.abort();
			}
			
			// Stop the MJPEG stream by clearing the image source
			if (imgRef.current) {
				imgRef.current.src = '';
			}
			
		} else {
			// Play
			isPausedRef.current = false;
			setIsPlaying(true);
			
			// Resume audio
			if (pcmPlayerRef.current) {
				pcmPlayerRef.current.Resume();
			}
			
			// Restart video stream with current time
			if (originalStart && originalStop) {
				const newStart = new Date(originalStart.getTime() + currentTime * 1000);
				
				// Fetch the new stream URL from API
				fetchStreamUrl(newStart, originalStop).then(streamData => {
					if (streamData?.session_url) {
						setCurrentUrl(streamData.session_url);
					}
				});
			}
			
			// If we're resuming, immediately start time tracking
			startTimeTracking();
		}
	}, [isPlaying, currentTime, originalStart, originalStop, stopTimeTracking, startTimeTracking, fetchStreamUrl]);

	const handleSliderChange = useCallback((event, newValue) => {
		setPendingSeekTime(newValue);
		setIsSeeking(true);
	}, []);

	const handleSliderChangeCommitted = useCallback((event, newValue) => {
		setIsSeeking(false);
		setPendingSeekTime(0);
		setCurrentTime(newValue);
		
		if (originalStart && originalStop) {
			const newStart = new Date(originalStart.getTime() + newValue * 1000);
			
			// Fetch the new stream URL from API
			fetchStreamUrl(newStart, originalStop).then(streamData => {
				if (streamData?.session_url) {
					setCurrentUrl(streamData.session_url);
				}
			});
		}
	}, [originalStart, originalStop, fetchStreamUrl]);

	const handleSkip = useCallback((seconds) => {
		const newTime = Math.max(0, Math.min(currentTime + seconds, duration));
		setCurrentTime(newTime);
		
		if (originalStart && originalStop) {
			const newStart = new Date(originalStart.getTime() + newTime * 1000);
			
			// Fetch the new stream URL from API
			fetchStreamUrl(newStart, originalStop).then(streamData => {
				if (streamData?.session_url) {
					setCurrentUrl(streamData.session_url);
				}
			});
		}
	}, [currentTime, duration, originalStart, originalStop, fetchStreamUrl]);

	const handlePlaybackSpeedChange = useCallback((newSpeed) => {
		// MJPEG streams don't support speed changes
		// This is a placeholder for future implementation
	}, []);

	return (
		<>
			{error ? <h3>{error}</h3> : null}

			<div className="relative overflow-hidden" ref={boxRef}>
				<img 
					className="rounded-lg history-video-player w-full h-full object-cover" 
					id="mjpeg-img" 
					ref={imgRef}
					alt="MJPEG Stream"
				/>
				{!isPlaying && (
					<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
						<div className="text-white text-lg font-semibold">Paused</div>
					</div>
				)}

				<div className="mx-auto relative">
					<H264Controls
						boxRef={boxRef}
						currentTime={isSeeking ? pendingSeekTime : currentTime}
						handleSkip={handleSkip}
						handleSliderChange={handleSliderChange}
						handleSliderChangeCommitted={handleSliderChangeCommitted}
						handleSliderChangeStart={() => setIsSeeking(true)}
						maxDuration={duration}
						onPlaybackSpeedChange={handlePlaybackSpeedChange}
						onVolumeChange={handleVolumeChange}
						playbackSpeed={100}
						playerRef={imgRef}
						room={roomData}
						handlePlayPause={handlePlayPause}
						isPlaying={isPlaying}
						isMjpeg={true}
					/>
				</div>
			</div>
		</>
	);
};

export default MjpegVideoPlayer;